import { useMutation } from "@apollo/client";
import { useState, useEffect } from "react";
import { Alert, Anchor, TextInput, Button, Space, Text, Title, PasswordInput, Progress } from "@mantine/core";
import { useMatch, useNavigate } from "react-router-dom";
import { IconLock, IconCheck, IconX } from "@tabler/icons-react";

import { RESET_PASSWORD_MUTATION } from "../../../queries/auth";

export default function ResetPasswordForm({ setMode }) {
  const routeMatch = useMatch('/reset-password/:resetToken');
  const resetToken = routeMatch?.params?.resetToken;
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const navigate = useNavigate();

  // Password strength calculator
  useEffect(() => {
    let strength = 0;
    if (password.length >= 6) strength += 25;
    if (password.length >= 10) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    setPasswordStrength(strength);
  }, [password]);

  const [resetPassword] = useMutation(RESET_PASSWORD_MUTATION, {
    onCompleted: (data) => {
      setLoading(false);
      if (data.resetPassword.status === 200) {
        setMessage({
          type: "success",
          text: "Your password has been successfully updated! Redirecting to login..."
        });
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setMessage({
          type: "error",
          text: data.resetPassword.error
        });
      }
    },
    onError: (err) => {
      setLoading(false);
      setMessage({
        type: "error",
        text: "Network error. Please try again."
      });
      console.error('Reset password error:', err);
    },
  });

  const handleResetPassword = () => {
    // Validation
    if (!password || !confirmPassword) {
      setMessage({
        type: "error",
        text: "Please fill in both password fields."
      });
      return;
    }

    if (password.length < 6) {
      setMessage({
        type: "error",
        text: "Password must be at least 6 characters long."
      });
      return;
    }

    if (password !== confirmPassword) {
      setMessage({
        type: "error",
        text: "Passwords do not match."
      });
      return;
    }

    if (!resetToken) {
      setMessage({
        type: "error",
        text: "Invalid or missing reset token. Please open the link from your email again."
      });
      return;
    }

    setLoading(true);
    setMessage(null);
    resetPassword({ variables: { token: resetToken, newPassword: password } });
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 50) return 'red';
    if (passwordStrength < 75) return 'yellow';
    return 'green';
  };

  const getPasswordStrengthLabel = () => {
    if (passwordStrength < 50) return 'Weak';
    if (passwordStrength < 75) return 'Medium';
    return 'Strong';
  };

  return (
    <div>
      <Title order={2} align="center" mb="md">
        Create New Password
      </Title>
      <Text align="center" color="dimmed" size="sm" mb="xl">
        Choose a strong password for your account.
      </Text>

      <PasswordInput
        icon={<IconLock size={16} />}
        placeholder="Enter your new password"
        label="New Password"
        size="md"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        disabled={loading}
        required
      />
      
      {password && (
        <>
          <Space h="xs" />
          <Progress 
            value={passwordStrength} 
            color={getPasswordStrengthColor()} 
            size="sm" 
          />
          <Text size="xs" color={getPasswordStrengthColor()} mt={5}>
            Password strength: {getPasswordStrengthLabel()}
          </Text>
        </>
      )}
      
      <Space h="md" />
      
      <PasswordInput
        icon={<IconLock size={16} />}
        placeholder="Confirm your new password"
        label="Confirm Password"
        size="md"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        disabled={loading}
        onKeyPress={(e) => e.key === 'Enter' && handleResetPassword()}
        required
      />
      
      {confirmPassword && (
        <Text 
          size="xs" 
          color={password === confirmPassword ? "green" : "red"} 
          mt={5}
        >
          {password === confirmPassword ? (
            <><IconCheck size={14} /> Passwords match</>
          ) : (
            <><IconX size={14} /> Passwords do not match</>
          )}
        </Text>
      )}
      
      <Space h="xl" />
      
      <Button
        fullWidth
        variant="filled"
        size="md"
        onClick={handleResetPassword}
        loading={loading}
        disabled={password !== confirmPassword || password.length < 6}
      >
        Reset Password
      </Button>
      
      <Space h="lg" />
      
      {message && (
        <>
          <Alert
            color={message.type === "error" ? "red" : "green"}
            title={message.type === "error" ? "Error" : "Success"}
            radius="md"
          >
            {message.text}
          </Alert>
          <Space h="lg" />
        </>
      )}
      
      <Text align="center">
        <Anchor onClick={() => navigate('/login')}>
          Go back to login
        </Anchor>
      </Text>
    </div>
  );
}
